import {
	Box,
	IconButton,
	Snackbar,
	TextField,
	Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { type SyntheticEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Alert } from "@mui/lab";
import { useFormsContext } from "../../../../context/FormsContext";
import {
	ButtonWithLoading,
	ErrorModal,
	useAssetContext,
	TimeService,
} from "@dexteel/mesf-core";
import { modalBoxSx } from "../../../modal-grid";
import type { Asset } from "@dexteel/mesf-core/dist/models/Asset";
import type { FormAsset } from "../../../../models/FormAsset";
import { ScheduleCodePicker } from "../picker/ScheduleCodePicker";
import { DateTimePicker } from "@mui/x-date-pickers";
import { getMomentTz } from "@dexteel/mesf-core";
import { INITIAL_VALUES_FORM_ASSET } from "@setup/configurations/forms/models/FormAssetInitialValue";
import { TransformAssetCodeModel } from "@/utils/transforms";
import { upsertFormAsset } from "@setup/configurations/forms/repositories/FormsRepository";
import EditIcon from "@mui/icons-material/Edit";
import cronstrue from "cronstrue";

const moment = getMomentTz();

type Props = {
	FormAsset: FormAsset;
};

export const EditFormAsset = ({ FormAsset }: Props) => {
	const [addSuccess, setAddSuccess] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState<string>("");
	const [initialValues, setInitialValues] = useState<FormAsset | null>(null);

	const {
		AssetId,
		AssetName,
		FormAssetId,
		SchedulePolicyCode,
		Periods,
		StartDate,
		FormId,
		CronExpression: initialCronExpression,
	} = FormAsset;

	const {
		state: { FormAsset: FormAssetFromContext },
		actions: { setAssetCodes, setAssetId, setAssetName, setFormAssetAssetName },
	} = useFormsContext();

	const {
		state: { allAssets },
	} = useAssetContext();

	const {
		control,
		handleSubmit,
		setValue,
		watch,
		formState: { isValid, errors },
		reset,
	} = useForm<FormAsset>({
		defaultValues: INITIAL_VALUES_FORM_ASSET,
	});

	const [cronExpression, setCronExpression] = useState<string>("");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isCustomSchedule, setIsCustomSchedule] = useState(false);
	const [formHasChanged, setFormHasChanged] = useState(false);

	const currentSchedulePolicyCode = watch("SchedulePolicyCode");

	// Watch all form values to detect changes
	const formValues = watch();

	const handleCancel = async () => {
		reset();
		setAssetId(1);
		setAssetName("Plant");
	};

	const buildTreeAssets = (nodes: any) => {
		if (nodes === undefined) return;
		// Tree build functions
		const idMapping = nodes.reduce((acc: any, el: any, i: number) => {
			acc[el.id] = i;
			return acc;
		}, {});
		let root;
		nodes.forEach((el: any) => {
			if (el.parentId === null) {
				root = el;
				return;
			}
			const parentEl = nodes[idMapping[el.parentId]];
			if (parentEl) {
				parentEl.children = [...(parentEl?.children || []), el];
			}
		});
		setAssetCodes(root as any);
	};

	const loadFilterAssets = () => {
		let mydataSource: any[] = [];
		allAssets.forEach((row: Asset) => {
			const code = TransformAssetCodeModel(row);
			mydataSource = [...mydataSource, code];
		});
		buildTreeAssets(mydataSource);
	};

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setAddSuccess(false);
	};

	const onSubmit = async (data: FormAsset) => {
		setIsSubmitLoading(true);
		const submitData = {
			...data,
			CronExpression: cronExpression || null,
		};

		const res = await upsertFormAsset(submitData);
		if (res.ok) {
			setIsSubmitLoading(false);
			setAddSuccess(true);

			// Update initial values after successful save
			setInitialValues({
				...submitData,
				CronExpression: cronExpression,
			});

			// Reset form change state
			setFormHasChanged(false);
		} else {
			setIsSubmitLoading(false);
			setError(res.message);
		}
	};

	const getAssetName = () => {
		const foundAsset = allAssets.find((asset) => asset.AssetId === AssetId);
		if (foundAsset) {
			setFormAssetAssetName(foundAsset.AssetName);
		}
	};

	const handleScheduleStartChange = (date: Date) => {
		setValue("StartDate", date);
	};

	// Check if schedule policy changed
	useEffect(() => {
		setIsCustomSchedule(currentSchedulePolicyCode === "P");
	}, [currentSchedulePolicyCode]);

	// Check if any form value has changed
	useEffect(() => {
		if (initialValues) {
			const hasChangedCron = initialCronExpression !== cronExpression;
			const hasChangedFields =
				initialValues.SchedulePolicyCode !== formValues.SchedulePolicyCode ||
				initialValues.Periods !== formValues.Periods ||
				initialValues.StartDate?.toString() !==
					formValues.StartDate?.toString();

			setFormHasChanged(hasChangedCron || hasChangedFields);
		}
	}, [formValues, cronExpression, initialValues]);

	useEffect(() => {
		if (FormAssetId) {
			handleCancel();
			reset();
			loadFilterAssets();
			setValue("AssetId", AssetId);
			setValue("SchedulePolicyCode", SchedulePolicyCode);
			setValue("Periods", Periods);
			setValue("StartDate", StartDate);
			setValue("FormId", FormId);
			setValue("FormAssetId", FormAssetId);
			setIsSubmitLoading(false);

			// Set cronExpression if it exists
			if (initialCronExpression) {
				setCronExpression(initialCronExpression);
			}

			// Update isCustomSchedule based on SchedulePolicyCode
			setIsCustomSchedule(SchedulePolicyCode === "P");

			// Store initial values for change detection
			setInitialValues({
				AssetId,
				AssetName,
				FormAssetId,
				SchedulePolicyCode,
				Periods,
				StartDate,
				FormId,
				CronExpression: initialCronExpression,
			});
		}
	}, []);

	// Update asset name when allAssets loads or AssetId changes
	useEffect(() => {
		if (AssetId && allAssets.length > 0) {
			getAssetName();
		}
	}, [allAssets, AssetId]);

	return (
		<>
			<Box
				component="form"
				onSubmit={handleSubmit(onSubmit)}
				sx={{ ...modalBoxSx, p: 2 }}
			>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
					<Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
						Asset: {FormAssetFromContext.AssetName || AssetName}
					</Typography>
					<Box sx={{ ml: "auto" }}>
						<ButtonWithLoading
							isLoading={isSubmitLoading}
							disabled={
								!isValid ||
								(currentSchedulePolicyCode === "P" &&
									(!cronExpression || cronExpression.trim().length === 0)) ||
								!formHasChanged
							}
							variant="contained"
							color="primary"
							type="submit"
						>
							Save
						</ButtonWithLoading>
					</Box>
				</Box>
				<Grid2 container spacing={2}>
					<Grid2 size={{ md: 4, xs: 12 }}>
						<Controller
							name="SchedulePolicyCode"
							control={control}
							render={({ field: { onChange, value } }) => (
								<ScheduleCodePicker
									value={value as string}
									onChange={onChange}
									cronExpression={cronExpression}
									onCronExpressionChange={setCronExpression}
									isModalOpen={isModalOpen}
									setIsModalOpen={setIsModalOpen}
									hideScheduleStart={setIsCustomSchedule}
									onScheduleStartChange={handleScheduleStartChange}
									scheduleStart={watch("StartDate")}
								/>
							)}
						/>
					</Grid2>
					<Grid2 size={{ md: 2, xs: 12 }}>
						<Controller
							name="Periods"
							control={control}
							rules={{ required: true, pattern: /^[0-9]*$/ }}
							render={({
								field: { onChange, value },
								fieldState: { error },
							}) => (
								<TextField
									margin="dense"
									label="Periods"
									variant="outlined"
									fullWidth
									autoComplete="off"
									value={value ?? ""}
									onChange={(evt) => {
										const newValue = evt.target.value.replace(/[^0-9]/g, "");
										if (newValue.length <= 99) onChange(newValue);
									}}
									error={!!error}
									helperText={error ? "Enter a period number" : ""}
									slotProps={{ inputLabel: { shrink: true } }}
								/>
							)}
						/>
					</Grid2>
					<Grid2 size={{ md: 6, xs: 12 }}>
						<Controller
							name="StartDate"
							control={control}
							render={({ field }) => (
								<DateTimePicker
									label="Start Date"
									timezone={
										isCustomSchedule
											? "UTC"
											: TimeService.getInstance().getServerTimeZone()
									}
									format={isCustomSchedule ? "MM/DD/YYYY" : "MM/DD/YYYY HH:mm"}
									timeSteps={{ minutes: 1 }}
									value={moment(field.value) as any}
									onChange={field.onChange}
									inputRef={field.ref}
									disabled={isCustomSchedule}
									slotProps={{
										textField: {
											fullWidth: true,
											variant: "outlined",
											margin: "dense",
											error: !!errors.StartDate,
											helperText: errors.StartDate?.message,
											InputLabelProps: { shrink: true },
										},
									}}
								/>
							)}
						/>
					</Grid2>
					{isCustomSchedule && cronExpression && (
						<Grid2 size={12}>
							<TextField
								label="Schedule"
								variant="outlined"
								fullWidth
								margin="dense"
								value={cronstrue.toString(cronExpression)}
								slotProps={{
									inputLabel: { shrink: true },
									input: {
										readOnly: true,
										endAdornment: (
											<IconButton
												edge="end"
												onClick={() => setIsModalOpen(true)}
												size="small"
											>
												<EditIcon />
											</IconButton>
										),
									},
								}}
							/>
						</Grid2>
					)}
				</Grid2>
			</Box>
			<Snackbar
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
				open={addSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
			>
				<Alert severity="info" onClose={handleClose}>
					Success
				</Alert>
			</Snackbar>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
